<script setup>
import {Link} from '@inertiajs/inertia-vue3';
import {Inertia} from "@inertiajs/inertia";
import {useHelpers} from "@/helpers";
import QuickAccessItem from "@/Components/Common/Helpers/QuickAccessItem.vue";
import {computed, ref} from "vue";

const props = defineProps({
  pageProps: {
    type: Object,
    default: null
  }
});

const sideBar = ref((window.localStorage.getItem('sidebar-toggle') || 'true') === 'true');
const appsMenu = ref(false);
const profileMenu = ref(false);
const currentUser = props.pageProps.auth.user;
const helpers = useHelpers();
const tenantManagerOnlyMode = computed(() => props.pageProps.app?.tenantManagerOnlyMode === true);
const navigationVisibility = props.pageProps?.app?.navigationVisibility || {};

const isMenuVisible = (key) => navigationVisibility?.[key] !== false;

const fullName = (user) => {
  let value = user.firstname;

  if (user.lastname) {
    value += ` ${user.lastname}`;
  }

  return value;
}

const logout = () => {
  Inertia.post(route('logout'));
};

const toggleSidebar = () => {
  window.dispatchEvent(new CustomEvent('sidebar-toggle-updated', {detail: sideBar.value = !sideBar.value}));
  window.localStorage.setItem('sidebar-toggle', sideBar.value ? 'true' : 'false');
}

const toggleApps = () => {
  profileMenu.value = false;
  appsMenu.value = !appsMenu.value;
}
const toggleProfile = () => {
  appsMenu.value = false;
  profileMenu.value = !profileMenu.value;
}

const outsideClickConfig = {
  handler: hideDropdownMenus,
  middleware: outsideMiddleware
}

function hideDropdownMenus() {
  if (appsMenu.value) {
    appsMenu.value = false;
  }
  if (profileMenu.value) {
    profileMenu.value = false;
  }
}

function outsideMiddleware(event) {
  return !event.target.id.includes('outside-dropdown');
}
</script>

<template>
  <header class="antialiased shadow">
    <nav class="bg-gray-50 border-gray-200 px-4 lg:px-6 py-2.5 dark:bg-gray-800">
      <div class="flex flex-wrap justify-between items-center">
        <div class="flex justify-start items-center">
          <button v-if="!tenantManagerOnlyMode" @click="toggleSidebar()" aria-expanded="true" aria-controls="sidebar"
                  class="p-2 mr-3 text-gray-600 rounded cursor-pointer lg:inline hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700">
            <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
                 viewBox="0 0 16 12" v-if="sideBar">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M1 1h14M1 6h14M1 11h7"/>
            </svg>
            <span class="w-5 h-5 fa fa-bars text-xl" v-else></span>
          </button>
        </div>
        <div class="flex items-center lg:order-2">
          <!-- Apps -->
          <button v-if="!tenantManagerOnlyMode" type="button" @click="toggleApps()" id="outside-dropdown-app-button"
                  class="p-2 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700 focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600">
            <!-- Icon -->
            <span class="h-4 w-4 text-xl fa fa-th-large text-cyan-500" id="outside-dropdown-app-span"></span>
          </button>
          <!-- Dropdown menu -->
          <div v-if="!tenantManagerOnlyMode" v-click-outside="outsideClickConfig"
               class="overflow-hidden z-50 my-4 max-w-sm text-base list-none bg-white rounded divide-y divide-gray-100 shadow-lg"
               id="apps-dropdown" :class="(appsMenu ? ['absolute', 'right-0', 'top-20'] : ['hidden'])">
            <div
              class="block py-2 px-4 text-base font-medium text-center text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              Quick Access
            </div>
            <div class="grid grid-cols-3 gap-4 p-4">
              <QuickAccessItem icon="calendar" label="Office Calendar" :href="route('internal.office-calendar.index')"
                               v-if="isMenuVisible('quick_access_office_calendar') && helpers.userHasPermission(currentUser, 'calendar')"/>
              <!-- <QuickAccessItem icon="barcode" label="Barcode Generator"
                               href="/content/barcode/barcode_generator.php"
                               v-if="helpers.userHasPermission(currentUser, 'barcode_generator')"/> -->
              <QuickAccessItem icon="clipboard" label="Create Label"
                               :href="route('admin.create-label.index')"
                               v-if="isMenuVisible('quick_access_create_label') && helpers.userHasPermission(currentUser, 'create_label')"/>
              <QuickAccessItem icon="users" label="Internal Directory"
                               :href="route('internal.directory.index')"
                               v-if="isMenuVisible('quick_access_internal_directory') && helpers.userHasPermission(currentUser, 'directory')"/>
              <!--<QuickAccessItem icon="envelope" label="Email"
                               href="http://mail.i2net.com/sqwebmail"
                               v-if="helpers.userHasPermission(currentUser, 'admin')"/>-->
              <QuickAccessItem icon="headset" label="Support"
                               :href="route('support.index')"
                               v-if="isMenuVisible('quick_access_support') && helpers.userHasPermission(currentUser, 'support')"/>
              <QuickAccessItem icon="server" label="Tenant Manager"
                               :href="route('admin.tenant-manager.index')"
                               v-if="isMenuVisible('quick_access_tenant_manager') && helpers.userHasPermission(currentUser, 'tenant_management')"/>
              <QuickAccessItem v-if="isMenuVisible('quick_access_www_site')" icon="globe" label="WWW Site" href="http://www.uniforms.net"/>
              <div v-if="isMenuVisible('quick_access_logout')" class="block p-4 text-center rounded-lg hover:bg-gray-100 group">
                <form @submit.prevent="logout">
                  <button class="text-sm font-medium text-cyan-500" type="submit">
                    <span
                      class="block fa text-xl fa-sign-out-alt mx-auto mb-2 w-5 h-5 text-cyan-500 group-hover:text-cyan-600">
                    </span>
                    <span class="block">Log Out</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
          <button type="button" @click="toggleProfile()" id="outside-dropdown-user-button"
                  class="flex mx-3 text-sm bg-gray-800 rounded-full md:mr-0 focus:ring-4 focus:ring-gray-300 dark:focus:ring-gray-600">
            <img class="w-8 h-8 rounded-full" :src="currentUser.profile_photo_url" :alt="fullName(currentUser)"
                 id="outside-dropdown-user-img">
          </button>
          <!-- Dropdown menu -->
          <div v-click-outside="outsideClickConfig"
               class="z-50 my-4 w-56 text-base list-none bg-white rounded divide-y divide-gray-100 shadow dark:bg-gray-700 dark:divide-gray-600"
               id="dropdown" :class="(profileMenu ? ['absolute', 'right-0', 'top-20'] : ['hidden'])">
            <!--<div class="py-3 px-4">
                <span class="block text-sm font-semibold text-gray-900 dark:text-white"
                      v-text="fullName(currentUser)"></span>
              <span class="block text-sm text-gray-500 truncate dark:text-gray-400"
                    v-text="currentUser.email"></span>
            </div>-->
            <ul v-if="!tenantManagerOnlyMode" class="py-1 text-gray-500 dark:text-gray-400" aria-labelledby="dropdown">
              <li>
                <Link :href="route('user.profile.show')"
                      class="block py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:text-gray-400 dark:hover:text-white">
                  My Profile
                </Link>
              </li>
            </ul>
            <ul class="py-1 text-gray-500 dark:text-gray-400" aria-labelledby="dropdown">
              <li>
                <form @submit.prevent="logout">
                  <button type="submit"
                          class="block py-2 px-4 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white w-full text-left">
                    Log Out
                  </button>
                </form>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>
